import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
    BaseContainer,
    FragmentId,
    NotificationType,
    RecordType,
    sessionStorageHelper,
    Share,
    ShLoadingComponent,
    ShSharesDataService
} from '@stylehaven/components';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { EMPTY, from, Observable } from 'rxjs';
import { RecordHolderService } from '@mediahaven/shared';

@Component({
    standalone: true,
    selector: 'med-sharing-redirect',
    template: '<div class="sh-page-section"><sh-loading [message]="\'ui.loading\' | translate"></sh-loading></div>',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, ShLoadingComponent]
})
export class SharingRedirectContainerComponent extends BaseContainer implements OnInit {
    private share$: Observable<Share>;

    // Injectors
    private readonly shareService: ShSharesDataService = inject(ShSharesDataService);
    private readonly recordHolder: RecordHolderService = inject(RecordHolderService);
    private readonly route: ActivatedRoute = inject(ActivatedRoute);
    private readonly router: Router = inject(Router);

    ngOnInit(): void {
        this.share$ = this.route.paramMap.pipe(
            map(params => params.get('shareId')),
            filter((shareId): shareId is string => {
                if (!shareId) {
                    throw new Error('Share ID not found');
                }
                return !!shareId;
            }),
            tap(shareId => sessionStorageHelper.set('shareId', shareId)),
            switchMap(shareId => this.shareService.getShareById(shareId)),
            catchError(err => {
                return this.resultNotFound().pipe(switchMap(() => EMPTY));
            })
        );

        this.share$
            .pipe(
                switchMap(share => this.initSingleRecordStream(share.FragmentId)),
                catchError(err => {
                    return this.resultNotFound(`/shared/${this.route.snapshot.paramMap.get('shareId')}`).pipe(
                        switchMap(() => EMPTY)
                    );
                }),
                take(1)
            )
            .subscribe();
    }

    private initSingleRecordStream(fragmentId: FragmentId): Observable<boolean> {
        return this.recordHolder.loadRecord(fragmentId).pipe(
            switchMap(record =>
                from(
                    this.router.navigate(
                        [
                            `${record.getMainRecordType() === RecordType.Selection ? 'selection' : 'single'}/${record.getFragmentId()}`
                        ],
                        {
                            relativeTo: this.route
                        }
                    )
                )
            )
        );
    }

    private resultNotFound(baseUrl: string = ''): Observable<boolean> {
        return from(this.router.navigateByUrl(`${baseUrl}/page-not-found`, { skipLocationChange: true }));
    }

    protected readonly NotificationType = NotificationType;
}
